/**
 * @module main
 * @description Application entry point - orchestrates all modules and initializes the application
 *
 * This is the main entry point for the Insightly Archive application.
 * It replaces the monolithic app.js with a modular architecture.
 *
 * Initialization sequence:
 * 1. Validate configuration and origin
 * 2. Import all necessary modules
 * 3. Initialize Firebase authentication
 * 4. Set up DOM references and UI components
 * 5. Set up authentication state listener
 * 6. Handle initial routing
 * 7. Export minimal global API for backwards compatibility
 */

// ===========================
// Configuration Validation
// ===========================

// Configuration validation - external files should be loaded before this script
if (typeof firebaseConfig === 'undefined') {
    logger.error('❌ firebaseConfig is missing. Did you forget to load firebase-config.js?');
    // Early alert before UI is initialized
    // eslint-disable-next-line no-alert -- Intentional: critical error before UI initialization
    window.alert('Configuration error: Firebase configuration not found. Please contact your administrator.');
    throw new Error('Firebase configuration missing');
}

if (typeof azureConfig === 'undefined') {
    logger.error('❌ azureConfig is missing. Did you forget to load azure-config.js?');
    // Early alert before UI is initialized
    // eslint-disable-next-line no-alert -- Intentional: critical error before UI initialization
    window.alert('Configuration error: Azure configuration not found. Please contact your administrator.');
    throw new Error('Azure configuration missing');
}

// Monitoring configuration is optional (for backwards compatibility)
const hasMonitoring = typeof monitoringConfig !== 'undefined';
if (!hasMonitoring) {
    logger.warn('⚠️ monitoringConfig not found. Application Insights will not be initialized.');
}

// ===========================
// Module Imports
// ===========================

// Core modules
import { initializeAuth, signInWithGoogle, signOutUser, onAuthStateChange } from './core/auth/auth-manager.js';
import { getEntityInfo, getFileId, getCurrentEntityType } from './core/router/url-router.js';
import { initializeApiClient } from './core/api/api-client.js';

// Monitoring service (safe wrapper)
import { initializeMonitoring, safeMonitoring, ErrorSeverity, ErrorCategory } from './services/monitoring-safe.js';

// UI Components
import { initializeDOMElements, getDOMElements } from './ui/dom/dom-manager.js';
import { showError } from './ui/components/error-display.js';
import { initErrorRecovery } from './ui/components/error-recovery.js';

// Feature modules
import { loadEntityFiles } from './features/entity/entity-loader.js';
import { showProjectSelection, showEntityTypeSelector, selectEntity } from './features/entity/entity-selector.js';
import { initializeUniversalSearch } from './features/search/universal-search.js';
import { initializeEntitySearch } from './features/search/entity-search.js';
import { initializeUploadElements } from './features/upload/upload-modal.js';
import { initializeUploadManager, showUploadModal } from './features/upload/upload-manager.js';
import { updateBreadcrumb, hideBreadcrumb } from './features/navigation/breadcrumb.js';
import { initializeFileSearch } from './features/files/file-search.js';
import { initializeRecentItems } from './features/navigation/recent-items.js';
import { initializeKeyboardShortcuts } from './features/navigation/keyboard-shortcuts.js';

// Utility modules
import { validateOrigin } from './utils/validators.js';
import { logger } from './utils/logger.js';
import { applyAppMode } from './utils/app-mode.js';

// ===========================
// Origin Validation
// ===========================

// Validate origin before proceeding
if (!validateOrigin()) {
    throw new Error('Unauthorized domain access');
}

// ===========================
// Application State
// ===========================

let currentUser = null;
let tokenRefreshInterval = null;

// ===========================
// Initialization Functions
// ===========================

/**
 * Initialize the application
 * Called when DOM is ready
 */
async function initializeApplication() {
    try {
        logger.info('🚀 Initializing Insightly Archive application...');

        // Step 0: Apply app mode (DC-Photos styling if applicable)
        applyAppMode();

        // Step 1: Initialize Application Insights (if available)
        if (hasMonitoring) {
            // Load the monitoring module dynamically
            const monitoringAvailable = await initializeMonitoring();

            if (monitoringAvailable) {
                // Initialize the monitoring service with configuration
                const initialized = safeMonitoring.initialize(monitoringConfig);

                if (initialized) {
                    logger.info('✅ Application Insights initialized');
                } else {
                    logger.warn('⚠️ Application Insights initialization failed');
                }
            } else {
                logger.warn('⚠️ Application Insights module not available');
            }

            // Expose monitoring globally for console testing (development only)
            // Always expose safeMonitoring (safe even if unavailable)
            if (!logger.isProduction()) {
                window.monitoring = safeMonitoring;
                window.ErrorCategory = ErrorCategory;
                window.ErrorSeverity = ErrorSeverity;
                logger.debug('Available globally: window.monitoring, window.ErrorCategory, window.ErrorSeverity');
            }
        }

        // Step 2: Initialize DOM references
        initializeDOMElements();

        // Step 3: Initialize API Client with Azure config
        initializeApiClient(azureConfig);
        logger.debug('✅ API Client initialized');

        // Step 4: Initialize Firebase
        await initializeAuth(firebaseConfig);
        logger.debug('✅ Firebase initialized');

        // Step 5: Set up event listeners
        setupEventListeners();
        logger.debug('✅ Event listeners set up');

        // Step 6: Initialize UI components
        initializeUploadElements();
        initializeUploadManager();
        initializeFileSearch();
        initializeRecentItems();
        initializeKeyboardShortcuts();
        initErrorRecovery(); // Initialize error recovery with connectivity monitoring
        logger.debug('✅ UI components initialized');

        // Step 7: Set up authentication state listener
        setupAuthStateListener();
        logger.debug('✅ Auth state listener active');

        // Step 8: Set up page view tracking for navigation
        if (hasMonitoring) {
            setupPageViewTracking();
            logger.debug('✅ Page view tracking active');
        }

        logger.info('✅ Application initialized successfully');

    } catch (error) {
        logger.error('❌ Failed to initialize application:', error);

        // Track initialization error
        if (hasMonitoring) {
            safeMonitoring.trackError(error, {
                category: ErrorCategory.UI,
                severity: ErrorSeverity.CRITICAL,
                operation: 'initialize_application'
            });
        }

        showError('Failed to initialize application. Please refresh the page.');
    }
}

/**
 * Set up all event listeners
 */
function setupEventListeners() {
    const dom = getDOMElements;

    // Sign in button
    if (dom.signInBtn) {
        dom.signInBtn.addEventListener('click', async () => {
            try {
                await signInWithGoogle();
            } catch (error) {
                logger.error('Sign in error:', error);

                // Track authentication error
                if (hasMonitoring) {
                    safeMonitoring.trackError(error, {
                        category: ErrorCategory.AUTHENTICATION,
                        severity: ErrorSeverity.ERROR,
                        operation: 'sign_in'
                    });
                }

                showError('Failed to sign in. Please try again.');
            }
        });
    }

    // Sign out button
    if (dom.signOutBtn) {
        dom.signOutBtn.addEventListener('click', async () => {
            try {
                await signOutUser();
            } catch (error) {
                logger.error('Sign out error:', error);

                // Track authentication error
                if (hasMonitoring) {
                    safeMonitoring.trackError(error, {
                        category: ErrorCategory.AUTHENTICATION,
                        severity: ErrorSeverity.WARNING,
                        operation: 'sign_out'
                    });
                }

                showError('Failed to sign out. Please try again.');
            }
        });
    }

    // Site title - return to home
    if (dom.siteTitle) {
        dom.siteTitle.addEventListener('click', async () => {
            if (currentUser) {
                // Clear URL parameters
                window.history.pushState({}, '', window.location.pathname);
                // Show project selection
                await showProjectSelection(currentUser);
            }
        });
    }
}

/**
 * Set up page view tracking for SPA navigation
 * Tracks initial page load and subsequent navigation via pushState/replaceState
 */
function setupPageViewTracking() {
    // Track initial page view
    trackCurrentPageView();

    // Override history.pushState to track navigation
    const originalPushState = window.history.pushState;
    window.history.pushState = function(...args) {
        originalPushState.apply(this, args);
        // Track page view after state change
        setTimeout(() => trackCurrentPageView(), 100);
    };

    // Override history.replaceState to track navigation
    const originalReplaceState = window.history.replaceState;
    window.history.replaceState = function(...args) {
        originalReplaceState.apply(this, args);
        // Track page view after state change
        setTimeout(() => trackCurrentPageView(), 100);
    };

    // Track browser back/forward navigation
    window.addEventListener('popstate', () => {
        setTimeout(() => trackCurrentPageView(), 100);
    });
}

/**
 * Track the current page view with Application Insights
 */
function trackCurrentPageView() {
    if (!hasMonitoring) {
        return;
    }

    const pageName = getPageName();
    const properties = {
        url: window.location.href,
        path: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash
    };

    // Add entity information if available
    const entityInfo = getEntityInfo();
    if (entityInfo) {
        properties.entityType = entityInfo.type;
        properties.entityId = entityInfo.id;
    }

    safeMonitoring.trackPageView(pageName, properties);
    logger.debug('📊 Page view tracked:', pageName);
}

/**
 * Get a friendly page name based on current URL/state
 * @returns {string} Page name for tracking
 */
function getPageName() {
    const entityInfo = getEntityInfo();
    const urlParams = new URLSearchParams(window.location.search);
    const typeParam = urlParams.get('type');
    const fileId = getFileId();

    if (entityInfo) {
        if (fileId) {
            return `${entityInfo.type} - File View`;
        }
        return `${entityInfo.type} - Entity Files`;
    } else if (typeParam) {
        return `Entity Type Search - ${typeParam}`;
    } else {
        return 'Home - Entity Selection';
    }
}

/**
 * Set up authentication state change listener
 */
function setupAuthStateListener() {
    onAuthStateChange(async (user) => {
        const dom = getDOMElements;

        // Clear any existing token refresh interval
        if (tokenRefreshInterval) {
            clearInterval(tokenRefreshInterval);
            tokenRefreshInterval = null;
        }

        if (user) {
            // Validate user domain at runtime
            const email = user.email;
            const domain = email.substring(email.lastIndexOf('@') + 1);

            if (domain !== 'shuttersup.co.uk') {
                showError('Access restricted to shuttersup.co.uk users only.');
                await signOutUser();
                return;
            }

            // User is signed in and authorized
            currentUser = user;

            // Set user context in Application Insights
            if (hasMonitoring) {
                safeMonitoring.setUserContext(user);
            }

            if (dom.userEmail) {
                dom.userEmail.textContent = user.email;
            }
            if (dom.userInfo) {
                dom.userInfo.classList.remove('hidden');
            }
            if (dom.signInSection) {
                dom.signInSection.classList.add('hidden');
            }

            // Set up proactive token refresh (every 50 minutes, tokens expire after 60)
            tokenRefreshInterval = setInterval(async () => {
                try {
                    await user.getIdToken(true);
                    logger.info('Token proactively refreshed');
                } catch (error) {
                    logger.error('Failed to refresh token:', error);
                }
            }, 50 * 60 * 1000); // 50 minutes

            // Initialize search functionality
            try {
                await initializeUniversalSearch(user);
                logger.debug('✅ Universal search initialized');
            } catch (error) {
                logger.error('Failed to initialize universal search:', error);
            }

            // Load entity files or show entity selection
            await handleInitialRouting(user);

        } else {
            // User is signed out
            currentUser = null;

            // Clear user context in Application Insights
            if (hasMonitoring) {
                safeMonitoring.clearUserContext();
            }

            if (dom.userInfo) {
                dom.userInfo.classList.add('hidden');
            }
            if (dom.signInSection) {
                dom.signInSection.classList.remove('hidden');
            }
            if (dom.loadingSection) {
                dom.loadingSection.classList.add('hidden');
            }
            if (dom.projectSelectionSection) {
                dom.projectSelectionSection.classList.add('hidden');
            }
            if (dom.filesSection) {
                dom.filesSection.classList.add('hidden');
            }

            // Hide breadcrumb when signed out
            hideBreadcrumb();
        }
    });
}

/**
 * Handle initial routing based on URL parameters
 * @param {Object} user - Firebase user object
 */
async function handleInitialRouting(user) {
    try {
        const entityInfo = getEntityInfo();
        const urlParams = new URLSearchParams(window.location.search);
        const typeParam = urlParams.get('type');

        // Check hostname for entity-specific landing
        const hostname = window.location.hostname.toLowerCase();

        if (entityInfo) {
            // Load specific entity (e.g., ?projectId=123)
            const fileId = getFileId();
            await loadEntityFiles(user, entityInfo.type, entityInfo.id, fileId);
        } else if (typeParam) {
            // Show entity type search (e.g., ?type=projects)
            await showEntityTypeSelector(typeParam, user);
        } else if (hostname.includes('dc-photos')) {
            // DC-Photos hostname detected - navigate to dc-photos entity type
            logger.info('DC-Photos hostname detected, navigating to dc-photos entity');
            await showEntityTypeSelector('dc-photos', user);
        } else {
            // Show entity selection (home page)
            await showProjectSelection(user);
        }
    } catch (error) {
        logger.error('Error handling initial routing:', error);
        showError('Failed to load content. Please try again.');
        await showProjectSelection(user);
    }
}

// ===========================
// Global API Export (Compatibility Layer)
// ===========================

/**
 * Export minimal global API for backwards compatibility
 *
 * These functions are exposed on the window object to maintain
 * compatibility with existing code and event handlers.
 *
 * ⚠️ IMPORTANT: Minimize additions to this list. Prefer using
 * module imports in new code.
 */

// Entity selection (used by search results and navigation)
window.selectEntity = selectEntity;

// Upload functions (used by entity metadata upload button)
window.showUploadModal = showUploadModal;

// Navigation functions (used by breadcrumb navigation)
window.showUnifiedEntitySelection = showProjectSelection;
window.showEntityTypeSearch = showEntityTypeSelector;
window.updateBreadcrumb = updateBreadcrumb;
window.hideBreadcrumb = hideBreadcrumb;

// Search functions (used by entity-selector.js)
window.initializeUniversalSearch = initializeUniversalSearch;
window.initializeEntitySearch = initializeEntitySearch;

// Export for debugging/console access
window.__app = {
    version: '2.0.0',
    modules: {
        auth: { signIn: signInWithGoogle, signOut: signOutUser },
        router: { getEntityInfo, getFileId, getCurrentEntityType },
        entity: { loadEntityFiles, showProjectSelection, selectEntity },
        search: { initializeUniversalSearch, initializeEntitySearch }
    },
    getCurrentUser: () => currentUser
};

// ===========================
// Application Entry Point
// ===========================

// Wait for DOM to be ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApplication);
} else {
    // DOM is already ready
    initializeApplication();
}

// Log successful module load
logger.debug('✅ main.js module loaded');
